import React, { useEffect, useRef, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { me, refresh } from "../crud/auth.crud";
import { actions, actionTypes } from "../store/ducks/auth.duck";
import { LayoutSplashScreen } from "../../_metronic";
import { getFallBackTimeZone } from "../utils/Utils";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

function AuthBoot({ children }) {
  const dispatch = useDispatch();
  const { me: meData, authToken } = useSelector((state) => state.auth);
  const refreshTimerRef = useRef(null);
  const hasBootedRef = useRef(false);
  const [isBooting, setIsBooting] = useState(true);

  const validateUserContext = (userData) => {
    if (userData.identity?.ctx !== "barbeiro") {
      return false;
    }

    if (userData.session?.origin !== "web") {
      return false;
    }

    return true;
  };

  const doRefresh = useCallback(async () => {
    try {
      await refresh();
    } catch (error) {
      const responseData = error.response?.data;
      const hasRefreshAction = responseData?.action === "refresh_required";

      if (hasRefreshAction) return;

      dispatch(actions.logout());
    }
  }, [dispatch]);

  const verifySession = useCallback(
    async (isRetryAfterRefresh = false) => {
      try {
        const response = await me();
        const userData = response.data;

        if (!validateUserContext(userData)) {
          console.warn("AuthBoot validation failed:", {
            ctx: userData.identity?.ctx,
            origin: userData.session?.origin,
          });
          dispatch(actions.logout());
          return;
        }

        localStorage.setItem("timeZone", userData.identity?.time_zone ?? getFallBackTimeZone());
        dispatch(actions.fulfillUser(userData));

        if (userData._migrate?.clearBearer) {
          dispatch({ type: actionTypes.ClearLegacyToken });
        }
      } catch (error) {
        console.error("AuthBoot /me error:", error);
        const responseData = error.response?.data;
        const hasRefreshAction = responseData?.action === "refresh_required";

        if (hasRefreshAction && !isRetryAfterRefresh) {
          console.log("AuthBoot: Token expired, attempting refresh...");
          try {
            await refresh();
            console.log("AuthBoot: Refresh successful, retrying /me...");
            await verifySession(true);
          } catch (refreshError) {
            console.error(
              "AuthBoot: Refresh failed, logging out",
              refreshError
            );
            dispatch(actions.logout());
          }
          return;
        }

        if (meData || authToken) {
          dispatch(actions.logout());
        }
      }
    },
    [dispatch, meData, authToken]
  );

  const startRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }

    refreshTimerRef.current = setInterval(doRefresh, REFRESH_INTERVAL_MS);
  }, [doRefresh]);

  const stopRefreshTimer = useCallback(() => {
    if (!refreshTimerRef.current) return;

    clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = null;
  }, []);

  useEffect(() => {
    if (!hasBootedRef.current) {
      hasBootedRef.current = true;
      setIsBooting(true);
      verifySession().finally(() => setIsBooting(false));
    }

    if (meData) {
      startRefreshTimer();
    } else {
      stopRefreshTimer();
    }

    return stopRefreshTimer;
  }, [meData, verifySession, startRefreshTimer, stopRefreshTimer]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!meData) return;

      doRefresh();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [meData, doRefresh]);

  if (isBooting) {
    return <LayoutSplashScreen />;
  }

  return children;
}

export default AuthBoot;
